import { Grid2, Box } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { CompanyDetailsResponse } from '@/models/payment';
import {
    SubTitle,
    Title,
    MDRegular,
    SMRegular,
    SMMedium,
    TextSMRegular
} from '@/typography/Typography';
import {
    PrimaryButton,
    SecondaryButtonRegular,
    SecondaryButtonSuccess
} from '@/components/shared/Button';
import { InputText } from '@/components/shared/InputText';
import { useNotificationsSnackbar } from '@/components/snackbar/NotificationsSnackbarContext';
import { useMasterCardConnectURL } from '@/services/paymentsQuery';
import {
    IntegrationCard,
    IntegrationCardPadding,
    GridContainer
} from '@/pages/company/componants/Base';
import { IOSSwitch } from '@/components/shared/Switch';
import { ButtonContainer } from '@/pages/company/PaymentSetting';
import Chip from '@/components/shared/Chip';
import { usePaymentsMutations } from '@/services/paymentsMutations';
import { useErrorHandling } from '@/utils/errorHandling';

export const AutoTopUpSettings = ({
    currentCompany,
    isAutoTopupEnabled,
    setIsAutoTopupEnabled,
    topUpAmount,
    setTopUpAmount
}: {
    currentCompany?: CompanyDetailsResponse;
    isAutoTopupEnabled: boolean;
    setIsAutoTopupEnabled: (value: boolean) => void;
    topUpAmount: number;
    setTopUpAmount: (value: number) => void;
}) => {
    const { t } = useTranslation();
    const { setAlert } = useNotificationsSnackbar();
    const { updateCompanyDetails } = usePaymentsMutations();
    const { backendErrorHandler } = useErrorHandling();

    const isConnected = currentCompany?.autoTopupStatus === 'Connected';

    // Prefetch MasterCard connect URL using React Query
    const { data: masterCardConnectData } = useMasterCardConnectURL(
        currentCompany?.paymentProvider === 'None' &&
            currentCompany?.paymentSource === 'InstaPaidAccount'
    );

    const handleConnectNow = async () => {
        try {
            if (masterCardConnectData?.data?.connectUrl) {
                window.open(masterCardConnectData.data.connectUrl, '_blank');
                setAlert(t('opening_mastercard_connection'), 'info');
            } else {
                setAlert(t('failed_get_connection_url'), 'error');
            }
        } catch (error) {
            console.error('Error connecting to MasterCard:', error);
            setAlert(t('failed_connect_mastercard'), 'error');
        }
    };

    return (
        <>
            <Box>
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        mb: (currentCompany?.autoTopupAmount ?? 0) > 0 ? 2 : 0
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 2
                        }}
                        pl={1}
                    >
                        <IOSSwitch
                            checked={isAutoTopupEnabled}
                            onChange={(e) => {
                                setIsAutoTopupEnabled(e.target.checked);
                            }}
                        />
                        <MDRegular>{t('activate_auto_topup')}</MDRegular>
                    </Box>
                </Box>

                {(currentCompany?.autoTopupAmount ?? 0) > 0 && (
                    <Box>
                        {isConnected && (
                            <Box
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    mb: 2,
                                    mt: 3,
                                    width: '100%'
                                }}
                            >
                                <Chip
                                    label={
                                        <SMMedium>
                                            {t('connected_with_id', {
                                                id: currentCompany?.customerId
                                            })}
                                        </SMMedium>
                                    }
                                    icon={<span></span>}
                                    color="success"
                                />
                            </Box>
                        )}
                        <SubTitle maxWidth={'80%'} mb={3}>
                            {t('activate_auto_topup_with_customer_id')}
                        </SubTitle>
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                mb: 2,
                                width: '100%'
                            }}
                        >
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center'
                                }}
                            >
                                <TextSMRegular mr={2} fontWeight={700}>
                                    {t('customer_id')}
                                </TextSMRegular>
                                <Chip
                                    label={
                                        <SMMedium>
                                            {currentCompany?.customerId}
                                        </SMMedium>
                                    }
                                    color="success"
                                />
                            </Box>
                            <SecondaryButtonSuccess
                                size="small"
                                onClick={handleConnectNow}
                            >
                                {t('connect_new_window')}
                            </SecondaryButtonSuccess>
                        </Box>

                        <InputText
                            type="number"
                            value={topUpAmount}
                            onChange={(e) => {
                                if (parseInt(e.target.value) > 0) {
                                    setTopUpAmount(parseInt(e.target.value));
                                }
                            }}
                            label={t('amount_to_topup')}
                            inputAdornment={
                                <SMRegular
                                    sx={{
                                        color: '#667085'
                                    }}
                                >
                                    {currentCompany?.currency || 'DKK'}
                                </SMRegular>
                            }
                        />
                    </Box>
                )}
            </Box>
        </>
    );
};
